import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { FunctionComponent } from "react";
import { InteractiveElement } from "@iventis/styles/src/components";
import { styled } from "@iventis/styles";
import { useTheme } from "@mui/material";
import { Theme } from "@emotion/react";
import { SimpleTooltip } from "./tooltips";
import { DisabledOverlay } from "./disabled-overlay";

export interface ToolConfig {
    icon: IconProp;
    label: string;
    disabled?: boolean;
    onClick: () => void;
}

export interface Tools {
    left: ToolConfig[];
    right: ToolConfig[];
}

interface ToolsFactoryProps {
    tools: Tools;
}

/**
 * A component which creates an icon button
 * @param {IconProp} icon - an icon to be displayed
 * @param {string} label - a text not to be displayed but used for screen readers
 * @param {boolean} disabled - if the button is disabled or not
 * @param {() => void} onClick - a function which handles when the component is clicked
 */
const ToolComponent: FunctionComponent<ToolConfig> = ({ icon, label, disabled, onClick }) => (
    <SimpleTooltip text={label} placement="top">
        <span style={{ position: "relative" }}>
            <InteractiveElement onClick={onClick} disabled={disabled} style={{ cursor: disabled ? "default" : "pointer" }}>
                <span className="hidden">{label}</span>
                {disabled && <DisabledOverlay backgroundColour="transparent" />}
                <FontAwesomeIcon style={{ height: "16px" }} icon={icon} />
            </InteractiveElement>
        </span>
    </SimpleTooltip>
);

/**
 * A component to produce tool components, has a left and right side where tools can be shown
 * @param {Tools} tools - an object containing a left and right property, both properties contain a ToolConfig array
 */
export const ToolBarComponent: FunctionComponent<ToolsFactoryProps> = ({ tools }) => {
    const theme = useTheme<Theme>();
    return (
        <ToolsContainer>
            <SubToolContainer justifyContent="flex-start" flexGrow="0" backgroundColour={theme.shades.three}>
                {tools.left.map((tool) => (
                    <ToolComponent icon={tool.icon} label={tool.label} key={tool.label} onClick={tool.onClick} disabled={tool.disabled} />
                ))}
            </SubToolContainer>
            <SubToolContainer justifyContent="flex-end" flexGrow="1" backgroundColour={theme.shades.four}>
                {tools.right.map((tool) => (
                    <ToolComponent icon={tool.icon} label={tool.label} key={tool.label} onClick={tool.onClick} disabled={tool.disabled} />
                ))}
            </SubToolContainer>
        </ToolsContainer>
    );
};

const ToolsContainer = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
`;

const SubToolContainer = styled.div<{ justifyContent: "flex-start" | "center" | "flex-end"; flexGrow: "0" | "1"; backgroundColour: string }>`
    display: flex;
    justify-content: ${({ justifyContent }) => justifyContent};
    flex-grow: ${({ flexGrow }) => flexGrow};
    background-color: ${({ backgroundColour }) => backgroundColour};
`;
